import React from 'react';
import { Flex, Icon, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface ProgressBarProps {
  status: number;
}

type LineColor = '#00AAFF' | '#DCE3E9';

interface StepVisualState {
  left: LineColor;
  right: LineColor;
  active: boolean;
  done: boolean;
}

const ACTIVE_COLOR = '#00AAFF';
const INACTIVE_COLOR = '#DCE3E9';
const ACTIVE_BORDER = '#E6F7FF';
const INACTIVE_TEXT = '#4F5E71';

const CheckGlyph: React.FC = () => (
  <Icon viewBox="0 0 16 16" boxSize="16px">
    <path d="M6.45 11.2L3.55 8.3L2.6 9.25L6.45 13.1L13.4 6.15L12.45 5.2L6.45 11.2Z" fill="#FFFFFF" />
  </Icon>
);

const getStepStates = (status: number): StepVisualState[] => {
  switch (status) {
    case 0:
      return [
        { left: ACTIVE_COLOR, right: INACTIVE_COLOR, active: true, done: false },
        { left: INACTIVE_COLOR, right: INACTIVE_COLOR, active: false, done: false },
        { left: INACTIVE_COLOR, right: INACTIVE_COLOR, active: false, done: false },
        { left: INACTIVE_COLOR, right: INACTIVE_COLOR, active: false, done: false },
      ];
    case 1:
      return [
        { left: ACTIVE_COLOR, right: ACTIVE_COLOR, active: false, done: true },
        { left: ACTIVE_COLOR, right: INACTIVE_COLOR, active: true, done: false },
        { left: INACTIVE_COLOR, right: INACTIVE_COLOR, active: false, done: false },
        { left: INACTIVE_COLOR, right: INACTIVE_COLOR, active: false, done: false },
      ];
    case 2:
      return [
        { left: ACTIVE_COLOR, right: ACTIVE_COLOR, active: false, done: true },
        { left: ACTIVE_COLOR, right: ACTIVE_COLOR, active: false, done: true },
        { left: ACTIVE_COLOR, right: INACTIVE_COLOR, active: true, done: false },
        { left: INACTIVE_COLOR, right: INACTIVE_COLOR, active: false, done: false },
      ];
    case 3:
    default:
      return [
        { left: ACTIVE_COLOR, right: ACTIVE_COLOR, active: false, done: true },
        { left: ACTIVE_COLOR, right: ACTIVE_COLOR, active: false, done: true },
        { left: ACTIVE_COLOR, right: ACTIVE_COLOR, active: false, done: true },
        { left: ACTIVE_COLOR, right: INACTIVE_COLOR, active: true, done: false },
      ];
  }
};

const ProgressBar: React.FC<ProgressBarProps> = ({ status }) => {
  const { t } = useTranslation();

  const steps = [
    { label: t('application.step1'), number: 1 },
    { label: t('application.step2'), number: 2 },
    { label: t('application.step3'), number: 3 },
    { label: t('application.step4'), number: 4 },
  ];

  const visuals = getStepStates(status);

  return (
    <Flex w="100%" justify="center">
      <Flex w="100%" maxW="750px" align="flex-start">
        {steps.map((step, index) => {
          const visual = visuals[index];
          const isCurrent = visual.active;

          return (
            <Flex key={step.number} flex="1" direction="column" align="center" gap="8px">
              {/* 線段與圓圈層 */}
              <Flex w="100%" align="center" position="relative" h="20px">
                {/* 左側線段 */}
                <Flex flex="1" h="2px" bg={visual.left} borderRadius={index === 0 ? '1px 0 0 1px' : '0'} />

                {/* 圓圈數字 */}
                <Flex
                  zIndex={1}
                  w="20px"
                  h="20px"
                  borderRadius="full"
                  bg={visual.active || visual.done ? ACTIVE_COLOR : INACTIVE_COLOR}
                  border={visual.active ? `3px solid ${ACTIVE_BORDER}` : 'none'}
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  {visual.done ? (
                    <CheckGlyph />
                  ) : (
                    <Text
                      fontFamily="'Open Sans'"
                      fontWeight={600}
                      fontSize="12px"
                      color={visual.active ? '#FFFFFF' : '#000000'}
                    >
                      {step.number}
                    </Text>
                  )}
                </Flex>

                {/* 右側線段 */}
                <Flex
                  flex="1"
                  h="2px"
                  bg={visual.right}
                  borderRadius={index === steps.length - 1 ? '0 1px 1px 0' : '0'}
                />
              </Flex>

              {/* 步驟文字標籤 */}
              <Text
                fontFamily="'Open Sans'"
                fontWeight={isCurrent ? 600 : 400}
                fontSize="14px"
                lineHeight="150%"
                letterSpacing="0.01em"
                color={isCurrent ? '#000000' : INACTIVE_TEXT}
                textAlign="center"
              >
                {step.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

export default ProgressBar;
